import { useState } from 'react'
import { useQuery } from 'react-query'
import { competitionApi } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import { 
  TrophyIcon, 
  CalendarIcon, 
  UserGroupIcon, 
  UserIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline'

interface Competition {
  id: number
  name: string
  description: string
  category: 'coptic_hymns' | 'coptic_language' | 'spiritual' | 'art' | 'sports'
  type: 'individual' | 'team'
  sport_type?: 'volleyball' | 'soccer' | 'chess' | 'ping_pong'
  age_group_name: string
  max_participants?: number
  registration_deadline: string
  start_date: string
  end_date: string
  is_active: boolean
  registered_count?: number
}

const categoryIcons = {
  coptic_hymns: '🎵',
  coptic_language: '📚',
  spiritual: '✝️',
  art: '🎨',
  sports: '⚽'
}

const categoryNames = {
  coptic_hymns: 'Coptic Hymns',
  coptic_language: 'Coptic Language',
  spiritual: 'Spiritual',
  art: 'Art',
  sports: 'Sports'
}

const sportTypeNames = {
  volleyball: 'Volleyball',
  soccer: 'Soccer',
  chess: 'Chess',
  ping_pong: 'Ping Pong'
}

const CompetitionsPage = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [filterCategory, setFilterCategory] = useState<string>('all')
  const [filterType, setFilterType] = useState<string>('all')
  const [showOnlyActive, setShowOnlyActive] = useState(true)

  const { data: competitions = [], isLoading, error } = useQuery(
    'competitions',
    competitionApi.getCompetitions,
    {
      select: (response) => response.data
    }
  )

  const filteredCompetitions = competitions.filter((competition: Competition) => {
    const matchesSearch = competition.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         competition.description?.toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesCategory = filterCategory === 'all' || competition.category === filterCategory
    const matchesType = filterType === 'all' || competition.type === filterType
    const matchesActive = !showOnlyActive || competition.is_active

    return matchesSearch && matchesCategory && matchesType && matchesActive
  })

  // Registration status check - to be implemented
  // const isRegistrationOpen = (competition: Competition) => {
  //   const now = new Date()
  //   const deadline = new Date(competition.registration_deadline)
  //   return deadline > now && competition.is_active
  // }

  const getRegistrationStatus = (competition: Competition) => {
    const now = new Date()
    const deadline = new Date(competition.registration_deadline)
    const startDate = new Date(competition.start_date)
    
    if (!competition.is_active) return 'inactive'
    if (now > new Date(competition.end_date)) return 'completed'
    if (now > startDate) return 'ongoing'
    if (now > deadline) return 'closed'
    return 'open'
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Unable to load competitions</h2>
          <p className="text-gray-600">Please try again later.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Competitions</h1>
          <p className="mt-2 text-gray-600">
            Explore and register for Mahragan competitions across different categories
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white p-6 rounded-lg shadow mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-2">
                Search Competitions
              </label>
              <input
                id="search"
                type="text"
                placeholder="Search by name or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
                Category
              </label>
              <select
                id="category"
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Categories</option>
                {Object.entries(categoryNames).map(([key, name]) => (
                  <option key={key} value={key}>{name}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-2">
                Competition Type
              </label>
              <select
                id="type"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Types</option>
                <option value="individual">Individual</option>
                <option value="team">Team</option>
              </select>
            </div>

            <div className="flex items-end">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={showOnlyActive}
                  onChange={(e) => setShowOnlyActive(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">Show only active</span>
              </label>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <TrophyIcon className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Total Competitions</dt>
                    <dd className="text-lg font-medium text-gray-900">{competitions.length}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckCircleIcon className="h-6 w-6 text-green-500" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Open Registration</dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {competitions.filter((c: Competition) => getRegistrationStatus(c) === 'open').length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ClockIcon className="h-6 w-6 text-yellow-500" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Ongoing</dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {competitions.filter((c: Competition) => getRegistrationStatus(c) === 'ongoing').length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <XCircleIcon className="h-6 w-6 text-red-500" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Completed</dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {competitions.filter((c: Competition) => getRegistrationStatus(c) === 'completed').length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Competitions Grid */}
        {filteredCompetitions.length === 0 ? (
          <div className="text-center py-12">
            <TrophyIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No competitions found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || filterCategory !== 'all' || filterType !== 'all'
                ? 'Try adjusting your search or filter criteria.'
                : 'Competitions will appear here once they are created.'
              }
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCompetitions.map((competition: Competition) => {
              const status = getRegistrationStatus(competition)
              const canRegister = status === 'open'
              
              return (
                <div
                  key={competition.id}
                  className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow"
                >
                  <div className="p-6">
                    {/* Header */}
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex items-center">
                        <span className="text-2xl mr-3">
                          {categoryIcons[competition.category]}
                        </span>
                        <div>
                          <span className="text-sm font-medium text-gray-500">
                            {categoryNames[competition.category]}
                          </span>
                          {competition.sport_type && (
                            <span className="text-sm text-gray-500 ml-2">
                              • {sportTypeNames[competition.sport_type]}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          status === 'open'
                            ? 'bg-green-100 text-green-800'
                            : status === 'ongoing'
                            ? 'bg-blue-100 text-blue-800'
                            : status === 'completed'
                            ? 'bg-gray-100 text-gray-800'
                            : status === 'closed'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {status === 'open' ? 'Registration Open' :
                         status === 'ongoing' ? 'In Progress' :
                         status === 'completed' ? 'Completed' :
                         status === 'closed' ? 'Registration Closed' : 'Inactive'}
                      </span>
                    </div>

                    {/* Competition Name */}
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      {competition.name}
                    </h3>

                    {/* Description */}
                    {competition.description && (
                      <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                        {competition.description}
                      </p>
                    )}

                    {/* Details */}
                    <div className="space-y-2 text-sm text-gray-600 mb-4">
                      <div className="flex items-center">
                        {competition.type === 'individual' ? (
                          <UserIcon className="h-4 w-4 mr-2 text-gray-400" />
                        ) : (
                          <UserGroupIcon className="h-4 w-4 mr-2 text-gray-400" />
                        )}
                        <span>{competition.type === 'individual' ? 'Individual' : 'Team'} Competition</span>
                      </div>

                      <div className="flex items-center">
                        <CalendarIcon className="h-4 w-4 mr-2 text-gray-400" />
                        <span>Age Group: {competition.age_group_name}</span>
                      </div>

                      <div className="flex items-center">
                        <ClockIcon className="h-4 w-4 mr-2 text-gray-400" />
                        <span>
                          Registration until: {new Date(competition.registration_deadline).toLocaleDateString()}
                        </span>
                      </div>

                      <div className="flex items-center">
                        <CalendarIcon className="h-4 w-4 mr-2 text-gray-400" />
                        <span>
                          Event: {new Date(competition.start_date).toLocaleDateString()} - {new Date(competition.end_date).toLocaleDateString()}
                        </span>
                      </div>
                    </div>

                    {/* Participants Info */}
                    <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                      <div className="text-sm text-gray-500">
                        {competition.registered_count !== undefined && (
                          <span>
                            {competition.registered_count} registered
                            {competition.max_participants && ` / ${competition.max_participants} max`}
                          </span>
                        )}
                      </div>
                      
                      <button
                        disabled={!canRegister}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                          canRegister
                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        {canRegister ? 'Register' : 
                         status === 'ongoing' ? 'In Progress' :
                         status === 'completed' ? 'View Results' :
                         status === 'closed' ? 'Registration Closed' : 'Inactive'}
                      </button>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Call to Action */}
        <div className="mt-12 text-center">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="text-lg font-medium text-blue-900 mb-2">
              Want to participate in competitions?
            </h3>
            <p className="text-blue-700 mb-4">
              Register your church and participants to join our exciting competitions.
            </p>
            <div className="space-x-4">
              <button className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors">
                Register as Participant
              </button>
              <button className="bg-white text-blue-600 border border-blue-600 px-6 py-2 rounded-md hover:bg-blue-50 transition-colors">
                Contact Organizers
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CompetitionsPage